import { Address, AddressDto } from '@bondsports/types';
import { RCAddress } from '../../types/entities/coreEntites/Address';

/**
 * Formats street components into a single line.
 * Bare numeric/alphanumeric apartment values get a `#` prefix; text-prefixed
 * values (e.g. "Apt 4B", "Suite 200") are kept as-is.
 *
 * @example formatStreet('123', 'William Street', '4B')      => '123 William Street #4B'
 * @example formatStreet('123', 'Main Street', 'Apt 4B')     => '123 Main Street Apt 4B'
 * @example formatStreet('123', 'Main Street', 'Suite 200')  => '123 Main Street Suite 200'
 */
export function formatStreet(streetNum?: string, street?: string, aptNum?: string): string {
	const formattedApt = aptNum ? formatAptNum(aptNum) : '';
	return [streetNum, street, formattedApt].filter(Boolean).join(' ');
}

function formatAptNum(aptNum: string): string {
	const trimmed = aptNum.trim();
	if (/^\d/.test(trimmed)) {
		return `#${trimmed}`;
	}
	return trimmed;
}

/**
 * Formats a given address object into a full comma-separated string.
 *
 * @example formatAddress({ streetNum: '123', street: 'William Street', city: 'New York', state: 'NY', zip: '10001', country: 'USA' })
 *          => '123 William Street, New York, NY 10001, USA'
 */
export function formatAddress(address: Omit<AddressDto, 'geo'>): string {
	const { streetNum = '', street = '', city = '', state = '', zip = '', country = '', aptNum = '' } = address;

	const formattedStreet = formatStreet(streetNum, street, aptNum);
	const formattedStateAndZip = [state, zip].filter(Boolean).join(' ');

	return [formattedStreet, city, formattedStateAndZip, country].filter(Boolean).join(', ');
}

/**
 * Retrieves the address string from the given RCAddress object.
 * House number and apartment live on streetNum and aptNum, separate from the street name.
 * @param address - The RCAddress object containing city, street number, street, apartment, state, and zip.
 * @returns The formatted address string.
 * @example
 * ```
 * const address: RCAddress = {
 *     city: 'New York',
 *     streetNum: '123',
 *     street: 'Main St',
 *     aptNum: '4B',
 *     state: 'NY',
 *     zip: '10001'
 * };
 * const formattedAddress = getAddressString(address);
 * console.log(formattedAddress); // Output: "New York, 123 Main St #4B, NY, 10001"
 * ```
 */
export const getAddressString = (address?: RCAddress) => {
	const { city, street, streetNum, aptNum, state, zip } = address ?? {};
	const streetLine = formatStreet(streetNum, street, aptNum);
	return [city, streetLine, state, zip].filter(Boolean).join(', ');
};

/**
 * Retrieves the street number, street, and city from the given RCAddress object and joins them into a string.
 * @param address - The RCAddress object containing street number, street, and city information.
 * @returns The formatted street and city string.
 * @example
 * ```
 * const address: RCAddress = {
 *     streetNum: '123',
 *     street: 'Main St',
 *     city: 'New York'
 * };
 * const streetAndCity = getAddressStreetAndCity(address);
 * console.log(streetAndCity); // Output: "123 Main St New York"
 * ```
 */
export const getAddressStreetAndCity = (address?: RCAddress) => {
	return [address?.streetNum, address?.street, address?.city].filter(Boolean).join(' ');
};

/**
 * Retrieves the zip code and state from the given RCAddress object and joins them into a string.
 * @param address - The RCAddress object containing zip code and state information.
 * @returns The formatted zip code and state string.
 * @example
 * ```
 * const address: RCAddress = {
 *     zip: '10001',
 *     state: 'NY'
 * };
 * const zipAndState = getAddressZipAndState(address);
 * console.log(zipAndState); // Output: "10001 NY"
 * ```
 */
export const getAddressZipAndState = (address?: RCAddress) => {
	return [address?.zip, address?.state].filter(Boolean).join(' ');
};

type IGeoJsonPoint = {
	type: string;
	coordinates: number[];
};

/**
 * Normalizes address geo from API responses (GeoJSON Point or number[]) into the
 * number[] shape expected by address update DTOs.
 */
export function normalizeAddressGeo(geo: unknown): number[] | undefined {
	if (Array.isArray(geo) && geo.every(coordinate => typeof coordinate === 'number')) {
		return geo;
	}

	if (isGeoJsonPoint(geo)) {
		return geo.coordinates;
	}

	return undefined;
}

function isGeoJsonPoint(geo: unknown): geo is IGeoJsonPoint {
	if (typeof geo !== 'object' || geo === null) {
		return false;
	}

	const candidate = geo as Partial<IGeoJsonPoint>;

	return (
		typeof candidate.type === 'string' &&
		Array.isArray(candidate.coordinates) &&
		candidate.coordinates.every(coordinate => typeof coordinate === 'number')
	);
}
